import { LineSegment } from '../graphics/line-segment.js';

export class MakeabilityLabLogo {

  static get DEFAULT_M_OUTLINE_COLOR()  { return 'black'; }
  static get DEFAULT_M_OUTLINE_STROKE_WIDTH() { return 4; }
  static get DEFAULT_L_OUTLINE_COLOR()  { return 'black'; }
  static get DEFAULT_L_OUTLINE_STROKE_WIDTH() { return 4; }

  constructor(x, y, triangleSize) {

    // The Makeability Lab logo is composed of 6 columns and 4 rows of square cells
    // Each cell is composed of two triangles, which can be in different orientations
    this.makeLabLogoArray = MakeabilityLabLogo.createMakeabilityLabLogoCellArray(x, y, triangleSize);

    this.visible = true;
    this.isMOutlineVisible = true;
    this.isLOutlineVisible = true;
    this.mOutlineColor = MakeabilityLabLogo.DEFAULT_M_OUTLINE_COLOR;
    this.mOutlineStrokeWidth = MakeabilityLabLogo.DEFAULT_M_OUTLINE_STROKE_WIDTH;
    this.lOutlineColor = MakeabilityLabLogo.DEFAULT_L_OUTLINE_COLOR;
    this.lOutlineStrokeWidth = MakeabilityLabLogo.DEFAULT_L_OUTLINE_STROKE_WIDTH;
    this.setColors('white', 'black');
    this.setFillColorsToDefault();

    for(const tri of this.getMShadowTriangles()){
      tri.fillColor = tri.strokeColor;
    }

    this.areLTriangleStrokesVisible = false;

    this.drawBoundingBox = false;

    // --- Outline opacity (0–1) for animated fade-in support ---
    this.lOutlineOpacity = 1.0;
    this.mOutlineOpacity = 1.0;

    // --- Label ---
    /** Whether the label is visible and included in the logo's bounding box / height. */
    this.isLabelVisible = false;
    this.labelText = "MAKEABILITY LAB";
    this.labelBoldUntilIndex = 4; // Bolds "MAKE"
    this.labelFontFamily = "Inter, Roboto, system-ui, -apple-system, sans-serif";
    this.labelColor = "black";

    /**
     * Vertical gap in pixels between the bottom of the logo grid and the label.
     * @type {number}
     */
    this.labelGap = 8;

    /**
     * Label font size as a fraction of logo width. Used for bounding-box and
     * height calculations (which don't have access to a canvas context).
     * At draw time the actual font size is computed via measureText so the
     * label spans exactly the logo width; this fraction should approximate
     * that rendered size so centering / layout is accurate.
     * @type {number}
     */
    this.labelFontSizeFraction = 0.09;
  }

  /**
   * The logo has 6 cols and 4 rows
   */
  static get numRows() { return 4; }

  /**
   * The logo has 6 cols and 4 rows
   */
  static get numCols() { return 6; }

  /**
   * Calculates the width of the MakeabilityLabLogo based on the size of the triangles.
   *
   * @param {number} triangleSize - The size of each triangle.
   * @returns {number} The total width of the MakeabilityLabLogo.
   */
  static getGridWidth(triangleSize){
    return MakeabilityLabLogo.numCols * triangleSize;
  }

  /**
   * Calculates the grid height of the MakeabilityLabLogo (excluding the label)
   * based on the size of the triangles.
   *
   * @param {number} triangleSize - The size of each triangle.
   * @returns {number} The grid height of the logo (numRows × triangleSize).
   */
  static getGridHeight(triangleSize){
    return MakeabilityLabLogo.numRows * triangleSize;
  }

  /**
 * Calculates the X origin for centering the MakeabilityLabLogo on a canvas.
 *
 * The returned value is the logo's grid origin (left edge of cell [0,0]),
 * not the visual center. Pass this directly to the MakeabilityLabLogo
 * constructor as `x`, and as `offsetX` to the Grid constructor.
 *
 * The optional strokePadding shifts the origin inward so the M outline's
 * stroke bleed (half the stroke width on each side) is visually symmetric.
 * Without it, the stroke bleeds further on the right than the left, making
 * the logo appear off-center. Pass MakeabilityLabLogo.DEFAULT_M_OUTLINE_STROKE_WIDTH
 * or the logo instance's mOutlineStrokeWidth.
 *
 * @param {number} triangleSize  - Cell size in pixels.
 * @param {number} canvasWidth   - Canvas width in logical (CSS) pixels.
 * @param {boolean} [alignToGrid=false] - If true, snaps to nearest cell boundary.
 * @param {number} [strokePadding=0] - Total stroke width of the M outline.
 *   The origin is shifted inward by strokePadding/2 (half on each side).
 * @returns {number} The x-coordinate for the logo's grid origin.
 */
static getGridXCenterPosition(triangleSize, canvasWidth, alignToGrid = false, strokePadding = 0) {
  // Subtract the full stroke padding from the available width so that
  // strokePadding/2 of margin remains on each side for the stroke bleed.
  const xCenter = (canvasWidth - MakeabilityLabLogo.getGridWidth(triangleSize) - strokePadding) / 2;

  if (alignToGrid) {
    return Math.round(xCenter / triangleSize) * triangleSize;
  }
  return xCenter;
}

/**
 * Calculates the Y origin for centering the MakeabilityLabLogo on a canvas.
 * Positions the grid only (excludes label). For label-aware centering use
 * the instance method centerLogo().
 *
 * See getGridXCenterPosition() for full documentation on strokePadding.
 *
 * @param {number} triangleSize  - Cell size in pixels.
 * @param {number} canvasHeight  - Canvas height in logical (CSS) pixels.
 * @param {boolean} [alignToGrid=false] - If true, snaps to nearest cell boundary.
 * @param {number} [strokePadding=0] - Total stroke width of the M outline.
 *   The origin is shifted inward by strokePadding/2 (half on each side).
 * @returns {number} The y-coordinate for the logo's grid origin.
 */
static getGridYCenterPosition(triangleSize, canvasHeight, alignToGrid = false, strokePadding = 0) {
  const yCenter = (canvasHeight - MakeabilityLabLogo.getGridHeight(triangleSize) - strokePadding) / 2;

  if (alignToGrid) {
    return Math.round(yCenter / triangleSize) * triangleSize;
  }
  return yCenter;
}

  /**
   * Sets the draw debug information flag for the logo and its components.
   *
   * @param {boolean} drawDebugInfo - A flag indicating whether to draw debug information.
   */
  setDrawDebugInfo(drawDebugInfo){
    this.drawBoundingBox = drawDebugInfo;
    for (let row = 0; row < this.makeLabLogoArray.length; row++) {
      for (let col = 0; col < this.makeLabLogoArray[row].length; col++) {
        this.makeLabLogoArray[row][col].setDrawDebugInfo(drawDebugInfo);
      }
    }
  }

  /**
   * Calculates the bounding box for the logo dynamically that encompasses all triangles
   * and accounts for stroke widths and label height (if visible). 
   * 
   * Keeps track of which triangles contribute to the minX, minY, maxX, and maxY values.
   *
   * @returns {Object} An object representing the bounding box with the following properties:
   * - `x` {number}: The minimum x-coordinate of the bounding box.
   * - `y` {number}: The minimum y-coordinate of the bounding box.
   * - `width` {number}: The width of the bounding box.
   * - `height` {number}: The height of the bounding box.
   * - `minXTriangle` {Triangle}: The triangle contributing to the minimum x-coordinate.
   * - `minYTriangle` {Triangle}: The triangle contributing to the minimum y-coordinate.
   * - `maxXTriangle` {Triangle}: The triangle contributing to the maximum x-coordinate.
   * - `maxYTriangle` {Triangle}: The triangle contributing to the maximum y-coordinate.
   */
  getBoundingBox() {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    let minXTriangle = null;
    let minYTriangle = null;
    let maxXTriangle = null;
    let maxYTriangle = null;

    for (const tri of this.getAllTriangles()) {
      if (!tri.visible){ continue; }

      const triBoundingBox = tri.getBoundingBox();
      
      // Update minX and minY
      if (triBoundingBox.x < minX) {
        minX = triBoundingBox.x;
        minXTriangle = tri;
      }
      if (triBoundingBox.y < minY) {
        minY = triBoundingBox.y;
        minYTriangle = tri;
      }
      
      // Update maxX and maxY
      if (triBoundingBox.x + triBoundingBox.width > maxX) {
        maxX = triBoundingBox.x + triBoundingBox.width;
        maxXTriangle = tri;
      }
      if (triBoundingBox.y + triBoundingBox.height > maxY) {
        maxY = triBoundingBox.y + triBoundingBox.height;
        maxYTriangle = tri;
      }
    }

    const padding = Math.max(this.mOutlineStrokeWidth, this.lOutlineStrokeWidth) / 2;

    // Extend bounding box to include the label when visible
    const labelExtra = this.labelHeight; // 0 when label is hidden

    return {
      x: minX - padding,
      y: minY - padding,
      width: (maxX - minX) + 2 * padding,
      height: (maxY - minY) + 2 * padding + labelExtra,
      minXTriangle,
      minYTriangle,
      maxXTriangle,
      maxYTriangle
    };
  }


  /**
   * Gets the far left x-coordinate of the Makeability Lab logo
   * 
   * @returns {number} The x-coordinate of the first element.
   */
  get x(){ return this.makeLabLogoArray[0][0].x }
  
  /**
   * Sets the x-coordinate for the logo by adjusting the coordinates 
   * of all triangles accordingly
   * 
   * @param {number} x - The new x-coordinate to set.
   */
  set x(x){
    const xOffset = x - this.x;
    for(const tri of this.getAllTriangles()){
      tri.x += xOffset;
    }
  }

  /**
   * Gets the top y-coordinate of the Makeability Lab logo
   * 
   * @returns {number} The y-coordinate of the first element.
   */
  get y(){ return this.makeLabLogoArray[0][0].y }

  /**
   * Sets the y-coordinate for the logo and adjusts the y-coordinates of all 
   * triangles accordingly.
   * 
   * @param {number} y - The new y-coordinate to set.
   */
  set y(y){ 
    const yOffset = y - this.y;
    for(const tri of this.getAllTriangles()){
      tri.y += yOffset;
    }
  }

  /**
   * Gets the size of a cell in the Makeability Lab logo
   * Each cell is composed of two triangles
   * 
   * @returns {number} The size of the cell.
   */
  get cellSize(){ return this.makeLabLogoArray[0][0].size }

  /**
   * Gets the width of the Makeability Lab logo
   *
   * @returns {number} The width of the Makeability Lab logo.
   */
  get width(){ return MakeabilityLabLogo.numCols * this.makeLabLogoArray[0][0].size }

  /**
   * Gets the height of the logo grid only (4 rows × cellSize), excluding the label.
   * @returns {number}
   */
  get logoGridHeight(){ return MakeabilityLabLogo.numRows * this.makeLabLogoArray[0][0].size }

  /**
   * Gets the approximate font size of the label in pixels.
   * Based on labelFontSizeFraction × logo width.
   * @returns {number}
   */
  get labelFontSize(){ return this.width * this.labelFontSizeFraction; }

  /**
   * Gets the vertical space the label occupies (gap + font size), or 0 if hidden.
   * @returns {number}
   */
  get labelHeight(){ return this.isLabelVisible ? this.labelGap + this.labelFontSize : 0; }

  /**
   * Gets the total height of the MakeabilityLab logo, including the label if visible.
   * @returns {number}
   */
  get height(){ return this.logoGridHeight + this.labelHeight; }

  /**
   * Getter for the default colors state.
   * 
   * @returns {boolean} - Returns true if the default colors are on, otherwise false.
   */
  get areDefaultColorsOn(){ return this._defaultColorsOn; }

  /**
   * Adjusts the logo size to fit within the given canvas dimensions.
   * Updated to fully account for stroke widths and label visibility.
   * @param {number} canvasWidth - The width of the canvas.
   * @param {number} canvasHeight - The height of the canvas.
   * @param {boolean} [alignToGrid=false] - Whether to align the center position to the grid.
   */
  fitToCanvas(canvasWidth, canvasHeight, alignToGrid = false) {
    // 1. Calculate the required padding based on the stroke widths.
    // We use the full stroke width as padding (half for each side of the canvas).
    const strokePadding = Math.max(this.mOutlineStrokeWidth, this.lOutlineStrokeWidth);

    // 2. Subtract stroke padding from both dimensions
    let adjustedWidth = canvasWidth - strokePadding;
    let adjustedHeight = canvasHeight - strokePadding;

    // 3. Account for the label if visible
    let effectiveRows = MakeabilityLabLogo.numRows;
    if (this.isLabelVisible) {
      adjustedHeight -= this.labelGap; //
      effectiveRows += MakeabilityLabLogo.numCols * this.labelFontSizeFraction; //
    }

    // 4. Calculate the triangle size based on the most restrictive dimension
    const triangleSize = Math.min(
      adjustedWidth / MakeabilityLabLogo.numCols,
      adjustedHeight / effectiveRows
    );

    // 5. Apply the size and center the logo within the original canvas area
    this.setTriangleSize(triangleSize);
    this.centerLogo(canvasWidth, canvasHeight, alignToGrid);
  }

  /**
   * Centers the logo on the canvas.
   *
   * @param {number} canvasWidth - The width of the canvas.
   * @param {number} canvasHeight - The height of the canvas.
   * @param {boolean} [alignToGrid=false] - Whether to align the logo to the grid.
   */
  centerLogo(canvasWidth, canvasHeight, alignToGrid=false){
    const xCenter = MakeabilityLabLogo.getGridXCenterPosition(this.cellSize, canvasWidth, alignToGrid);

    // Center the full height (grid + label) vertically
    let yCenter = (canvasHeight - this.height) / 2;
    if (alignToGrid) {
      yCenter = Math.round(yCenter / this.cellSize) * this.cellSize;
    }
    this.setLogoPosition(xCenter, yCenter);
  }

  /**
   * Sets the size of the logo based on the given width.
   *
   * @param {number} logoWidth - The width of the logo.
   */
  setLogoSize(logoWidth){
    const triangleSize = logoWidth / MakeabilityLabLogo.numCols;
    this.setTriangleSize(triangleSize);
  }

  /**
   * Sets the size of all triangles to the specified value.
   *
   * @param {number} triangleSize - The new size to set for all triangles.
   */
  setTriangleSize(newSize){
    const oldSize = this.cellSize;
    if (oldSize === newSize) return;
    const originX = this.x, originY = this.y;
    for (const tri of this.getAllTriangles()) {
      tri.x = originX + (tri.x - originX) * (newSize / oldSize);
      tri.y = originY + (tri.y - originY) * (newSize / oldSize);
      tri.size = newSize;
    }
  }

  /**
   * Sets the position of the logo by adjusting the coordinates of all triangles.
   *
   * @param {number} x - The new x-coordinate for the logo.
   * @param {number} y - The new y-coordinate for the logo.
   */
  setLogoPosition(x, y){
    const xOffset = x - this.x;
    const yOffset = y - this.y;
    for(const tri of this.getAllTriangles()){
      tri.x += xOffset;
      tri.y += yOffset;
    }
  }

  /**
   * Sets the visibility of the strokes for the L outline in the Makeability Lab logo
   * 
   * @param {boolean} visible - A boolean indicating whether the strokes should be visible.
   */
  set areLTriangleStrokesVisible(visible){ 
    for(const tri of this.getLTriangles()){
      tri.isStrokeVisible = visible;
    }
  }
  
  /**
   * Returns true of the L strokes are visible, otherwise false.
   * 
   * @returns {boolean} True if all L-shaped triangle strokes are visible, otherwise false.
   */
  get areLTriangleStrokesVisible() {
    return this.getLTriangles().every(tri => tri.isStrokeVisible);
  }

  /**
   * Sets the stroke color for all L-shaped triangles.
   *
   * @param {string} color - The color to set as the stroke color for the triangles.
   */
  setLTriangleStrokeColor(color){
    for(const tri of this.getLTriangles()){
      tri.strokeColor = color;
    }
  }

  /**
   * Sets the fill color for all L-shaped triangles. Accepts any CSS color,
   * including semi-translucent values (e.g. 'rgba(255, 255, 255, 0.5)') to let
   * the colored logo show through the L.
   *
   * @param {string} color - The color to set as the fill color for the triangles.
   */
  setLTriangleFillColor(color){
    for(const tri of this.getLTriangles()){
      tri.fillColor = color;
    }
  }

  /**
   * Sets the fill color for all MShadow triangles.
   *
   * @param {string} color - The color to set for the fill of the MShadow triangles.
   */
  setMShadowTriangleFillColor(color){
    for(const tri of this.getMShadowTriangles()){
      tri.fillColor = color;
    }
  }

  /**
   * Sets the stroke visibility for all triangles.
   *
   * @param {boolean} isTransparent - If true, the stroke will be made transparent (invisible).
   * @param {boolean} [includeMShadowTriangles=true] - If true, includes M shadow triangles in the operation.
   */
  setStrokeTransparent(isTransparent, includeMShadowTriangles=true){
    for (const tri of this.getAllTriangles(includeMShadowTriangles)) {
      tri.isStrokeVisible = !isTransparent;
    }
  }

  /**
   * Sets the internal triangles to transparent
   * @param {Boolean} isTransparent 
   * @param {Boolean} includeMShadowTriangles 
   */
  setFillTransparent(isTransparent, includeMShadowTriangles=true){
    for (const tri of this.getAllTriangles(includeMShadowTriangles)) {
      tri.isFillVisible = !isTransparent;
    }
  }

  /**
   * Sets the fill color for all triangles in the logo.
   *
   * @param {string} color - The color to set as the fill color for the triangles.
   */
  setFillColor(color, includeMShadowTriangles=true){
    for (const tri of this.getAllTriangles(includeMShadowTriangles)) {
      tri.fillColor = color;
    }
  }

  /**
   * Convenience method to set fill and stroke colors
   * @param {*} fillColor 
   * @param {*} strokeColor 
   */
  setColors(fillColor, strokeColor, includeMShadowTriangles=true){
    for (const tri of this.getAllTriangles(includeMShadowTriangles)) {
      tri.fillColor = fillColor;
      tri.strokeColor = strokeColor;
    }
  }

  /**
   * Retrieves all triangles from the Makeability Lab logo array.
   * The M shadow triangles are the two dark triangles on the bottom left and right
   * side of the logo
   * 
   * @param {boolean} [includeMShadowTriangles=true] - Whether to include M shadow triangles in the result.
   * @returns {Array} An array containing all the triangles from the Makeability Lab logo.
   */
  getAllTriangles(includeMShadowTriangles=true, includeLTriangles=true){
    let allTriangles = new Array();
    for (let row = 0; row < this.makeLabLogoArray.length; row++) {
      for (let col = 0; col < this.makeLabLogoArray[row].length; col++) {
        if ((includeMShadowTriangles || !MakeabilityLabLogo.isMShadowTriangle(row, col, 1)) &&
            (includeLTriangles || !MakeabilityLabLogo.isLTriangle(row, col, 1))) {
            allTriangles.push(this.makeLabLogoArray[row][col].tri1);
        }

        if ((includeMShadowTriangles || !MakeabilityLabLogo.isMShadowTriangle(row, col, 2)) &&
            (includeLTriangles || !MakeabilityLabLogo.isLTriangle(row, col, 2))) {
            allTriangles.push(this.makeLabLogoArray[row][col].tri2);
        }
      }
    }  
    return allTriangles;
  }

  /**
   * Gets the triangles that are part of the M "shadow". That is, the 
   * black/darkened parts of the M logo
   *
   * @returns {Array} An array containing the selected triangles.
   */
  getMShadowTriangles(){
    let mShadowTriangles = new Array();
    
    // left side
    mShadowTriangles.push(this.makeLabLogoArray[2][1].tri2);
    mShadowTriangles.push(this.makeLabLogoArray[3][1].tri1);
    
    // right side
    mShadowTriangles.push(this.makeLabLogoArray[2][4].tri2);
    mShadowTriangles.push(this.makeLabLogoArray[3][4].tri1);

    return mShadowTriangles;
  }

  /**
   * Gets the triangles that compose the L in the Makeability Lab logo
   *
   * @returns {Array} An array containing the selected triangles.
   */
  getLTriangles(){
    let lTriangles = new Array();
    lTriangles.push(this.makeLabLogoArray[0][0].tri2);
    lTriangles.push(this.makeLabLogoArray[0][1].tri2);
    lTriangles.push(this.makeLabLogoArray[1][0].tri1);
    lTriangles.push(this.makeLabLogoArray[1][1].tri1);
    lTriangles.push(this.makeLabLogoArray[1][1].tri2);
    lTriangles.push(this.makeLabLogoArray[1][2].tri2);
    lTriangles.push(this.makeLabLogoArray[2][1].tri1);
    lTriangles.push(this.makeLabLogoArray[2][2].tri1);
    lTriangles.push(this.makeLabLogoArray[2][2].tri2);
    lTriangles.push(this.makeLabLogoArray[3][2].tri1);
    lTriangles.push(this.makeLabLogoArray[3][3].tri1);

    lTriangles.push(this.makeLabLogoArray[2][3].tri1);
    lTriangles.push(this.makeLabLogoArray[2][3].tri2);

    lTriangles.push(this.makeLabLogoArray[1][3].tri2);
    lTriangles.push(this.makeLabLogoArray[2][4].tri1);
    lTriangles.push(this.makeLabLogoArray[1][4].tri2);
    return lTriangles;
  }

  /**
   * Gets the triangles that are colored in the ML logo by default
   *
   * @returns {Array} An array containing the default colored triangles.
   */
  getDefaultColoredTriangles(){
    let cTriangles = new Array();
    cTriangles.push(this.makeLabLogoArray[0][4].tri2);
    cTriangles.push(this.makeLabLogoArray[0][5].tri2);
    cTriangles.push(this.makeLabLogoArray[1][0].tri2);
    cTriangles.push(this.makeLabLogoArray[1][4].tri1);
    cTriangles.push(this.makeLabLogoArray[1][5].tri1);
    cTriangles.push(this.makeLabLogoArray[1][5].tri2);
    cTriangles.push(this.makeLabLogoArray[2][0].tri1);
    cTriangles.push(this.makeLabLogoArray[2][0].tri2);
    cTriangles.push(this.makeLabLogoArray[2][5].tri1);
    cTriangles.push(this.makeLabLogoArray[2][5].tri2);
    cTriangles.push(this.makeLabLogoArray[3][0].tri1);
    cTriangles.push(this.makeLabLogoArray[3][5].tri1);
    return cTriangles;
  }

  /**
   * Sets the default colors for the logo.
   */
  setFillColorsToDefault(){
    this.setDefaultColoredTrianglesFillColor(ORIGINAL_COLOR_ARRAY);
    this._defaultColorsOn = true;
  }

  /**
   * Sets the default fill color for the colored triangles.
   * 
   * @param {(string|string[])} fillColorOrColorArray - A single color string or an 
   * array of color strings to set as the fill color(s) for the triangles.
   */
  setDefaultColoredTrianglesFillColor(fillColorOrColorArray){
    const cTriangles = this.getDefaultColoredTriangles();
    if(Array.isArray(fillColorOrColorArray)){
      for(let i=0; i<cTriangles.length; i++){
        cTriangles[i].fillColor = fillColorOrColorArray[i];
      }
    }else{
      for(let i=0; i<cTriangles.length; i++){
        cTriangles[i].fillColor = fillColorOrColorArray;
      }
    }
  }

  /**
   * Sets the stroke width for all triangles.
   *
   * @param {number} strokeWidth - The width of the stroke to set.
   * @param {boolean} [includeMShadowTriangles=true] - Whether to include M shadow triangles.
   * @param {boolean} [includeLTriangles=true] - Whether to include L triangles.
   */
  setStrokeWidth(strokeWidth, includeMShadowTriangles=true, includeLTriangles=true){
    for(const tri of this.getAllTriangles(includeMShadowTriangles, includeLTriangles)){
      tri.strokeWidth = strokeWidth;
    }
  }

  /**
   * Draws the Makeability Lab logo and its outlines if they are visible.
   * 
   * This method performs the following actions:
   * 1. Checks if the logo is visible; if not, it returns immediately.
   * 2. Iterates through the `makeLabLogoArray` and calls the `draw` method on each element.
   * 3. If the M outline is visible, it draws the M outline using the specified color and stroke width.
   * 4. If the L outline is visible, it draws the L outline using the specified color and stroke width.
   */
  draw(ctx) {
    if(!this.visible){ return; }

    if(this.drawBoundingBox){
      // for debugging
      const bBox = this.getBoundingBox();
      ctx.save();
      ctx.setLineDash([4, 8]); // Dots of 3 pixel, gaps of 4 pixels
      ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
      ctx.strokeRect(bBox.x, bBox.y, bBox.width, bBox.height);
      ctx.restore();

      bBox.minXTriangle.visible = true;
      bBox.maxXTriangle.visible = true;
      bBox.minYTriangle.visible = true;
      bBox.maxYTriangle.visible = true;

      bBox.minXTriangle.fillColor = 'green';
      bBox.maxXTriangle.fillColor = 'blue';
      bBox.minYTriangle.fillColor = 'red';
      bBox.maxYTriangle.fillColor = 'orange';

      // set text properties
      ctx.fillStyle = 'black';
      ctx.font = '16px Arial';

      // show width and height of bounding box
      const boundingBoxDimensionsText = `Bounding Box Dimensions: ${bBox.width.toFixed(1)} x ${bBox.height.toFixed(1)}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(boundingBoxDimensionsText, bBox.x + bBox.width / 2, bBox.y);

      // measure and draw bounding box width centered at the top
      const widthText = `Width: ${bBox.width.toFixed(1)}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(widthText, bBox.x + bBox.width / 2, bBox.y + bBox.height + 2);

      // measure and draw bounding box height rotated 90 degrees and centered vertically
      const heightText = `Height: ${bBox.height.toFixed(1)}`;
      ctx.save();
      ctx.translate(bBox.x - 1, bBox.y + bBox.height / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(heightText, 0, 0);
      ctx.restore();
    }

    for (let row = 0; row < this.makeLabLogoArray.length; row++) {
      for (let col = 0; col < this.makeLabLogoArray[row].length; col++) {
          this.makeLabLogoArray[row][col].draw(ctx);
      }
    }

    this.drawMOutline(ctx);
    this.drawLOutline(ctx);

    this.drawLabel(ctx);
    
  }

  // ---------------------------------------------------------------------------
  // Outline drawing — public focused methods
  //
  // These draw only their respective outline stroke, respecting the visibility
  // flag and opacity property. They do not draw triangles, the other outline,
  // or the label.
  //
  // Useful for callers (e.g. MakeabilityLabLogoMorpher) that manage their own
  // triangle rendering and need the outlines as clean overlays without the
  // side-effects of calling the full draw() method.
  // ---------------------------------------------------------------------------

  /**
   * Draws only the M outline stroke.
   * No-ops when isMOutlineVisible is false or mOutlineOpacity is 0.
   *
   * @param {CanvasRenderingContext2D} ctx
   */
  drawMOutline(ctx) {
    if (!this.isMOutlineVisible || this.mOutlineOpacity <= 0) return;

    ctx.save();
    ctx.globalAlpha = this.mOutlineOpacity;
    ctx.strokeStyle = this.mOutlineColor;
    ctx.lineWidth   = this.mOutlineStrokeWidth;
    ctx.beginPath();
    for (const [x, y] of this.getMOutlinePoints()) {
      ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Draws only the L outline stroke.
   * No-ops when isLOutlineVisible is false or lOutlineOpacity is 0.
   *
   * @param {CanvasRenderingContext2D} ctx
   */
  drawLOutline(ctx) {
    if (!this.isLOutlineVisible || this.lOutlineOpacity <= 0) return;

    ctx.save();
    ctx.globalAlpha = this.lOutlineOpacity;
    ctx.strokeStyle = this.lOutlineColor;
    ctx.lineWidth   = this.lOutlineStrokeWidth;
    ctx.beginPath();
    for (const [x, y] of this.getLOutlinePoints()) {
      ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Draws the "MAKEABILITY LAB" label below the logo grid, with the first
   * `labelBoldUntilIndex` characters rendered in bold. The font is scaled so
   * the full label spans exactly the logo width.
   *
   * Callers can pass optional overrides for animated rendering (e.g. fade-in
   * or slide-up effects) without mutating the logo's own properties.
   *
   * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
   * @param {Object} [options]
   * @param {number} [options.opacity=1.0] - Label opacity (0–1).
   * @param {number} [options.yOffset=0]  - Additional vertical offset in pixels
   *                                         (positive = downward).
   */
  drawLabel(ctx, { opacity = 1.0, yOffset = 0 } = {}) {
    if (!this.isLabelVisible || opacity <= 0) return;

    const part1 = this.labelText.substring(0, this.labelBoldUntilIndex);
    const part2 = this.labelText.substring(this.labelBoldUntilIndex);

    // Measure at a reference size and scale to span the logo width exactly
    const testFontSize = 100;
    ctx.save();

    ctx.font = `bold ${testFontSize}px ${this.labelFontFamily}`;
    const widthPart1 = ctx.measureText(part1).width;
    ctx.font = `${testFontSize}px ${this.labelFontFamily}`;
    const widthPart2 = ctx.measureText(part2).width;

    const totalMeasuredWidth = widthPart1 + widthPart2;
    const fontSize = (this.width / totalMeasuredWidth) * testFontSize;

    // Position: below the grid, offset by gap + font size (alphabetic baseline)
    const labelY = this.y + this.logoGridHeight + this.labelGap + fontSize + yOffset;

    ctx.globalAlpha = opacity;
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = this.labelColor;

    // Bold part
    ctx.font = `bold ${fontSize}px ${this.labelFontFamily}`;
    ctx.textAlign = 'left';
    ctx.fillText(part1, this.x, labelY);

    // Regular part
    const part1ActualWidth = ctx.measureText(part1).width;
    ctx.font = `${fontSize}px ${this.labelFontFamily}`;
    ctx.fillText(part2, this.x + part1ActualWidth, labelY);

    ctx.restore();
  }

  /**
   * 
   * @returns gets the L outline as an array of line segments
   */
  getLOutlineLineSegments(){
    let lLineSegments = new Array();
    
    lLineSegments.push(new LineSegment(this.x + this.cellSize, this.y, 
      this.x + 3 * this.cellSize, this.y + 2 * this.cellSize));
    lLineSegments.push(new LineSegment(this.x + 3 * this.cellSize, 
      this.y + 2 * this.cellSize, this.x + 4 * this.cellSize, this.y + this.cellSize));
    lLineSegments.push(new LineSegment(this.x + 4 * this.cellSize, this.y + this.cellSize,
      this.x + 5 * this.cellSize, this.y + 2 * this.cellSize));
    lLineSegments.push(new LineSegment(this.x + 5 * this.cellSize, this.y + 2 * this.cellSize,
      this.x + 3 * this.cellSize, this.y + 4 * this.cellSize));
    lLineSegments.push(new LineSegment(this.x + 3 * this.cellSize, this.y + 4 * this.cellSize,
      this.x, this.y + 1 * this.cellSize));
    lLineSegments.push(new LineSegment(this.x, this.y + this.cellSize,
      this.x + this.cellSize, this.y));

    return lLineSegments;
  }

  /**
   * 
   * @returns Gets the L outline as an array of points (each point is [x, y])
   */
  getLOutlinePoints(){
    let lPoints = new Array();

    // Top part
    lPoints.push([this.x, this.y + this.cellSize]);
    lPoints.push([this.x + this.cellSize, this.y]);
    lPoints.push([this.x + 2 * this.cellSize, this.y + this.cellSize]);
    lPoints.push([this.x + 3 * this.cellSize, this.y + 2 * this.cellSize]);
    lPoints.push([this.x + 4 * this.cellSize, this.y + this.cellSize]);

    // Right side
    lPoints.push([this.x + 5 * this.cellSize, this.y + 2 * this.cellSize]);
    lPoints.push([this.x + 4 * this.cellSize, this.y + 3 * this.cellSize]);
    lPoints.push([this.x + 3 * this.cellSize, this.y + 4 * this.cellSize]);

    // Bottom part
    lPoints.push([this.x + 2 * this.cellSize, this.y + 3 * this.cellSize]);
    lPoints.push([this.x + 1 * this.cellSize, this.y + 2 * this.cellSize]);
    lPoints.push([this.x + 0 * this.cellSize, this.y + 1 * this.cellSize]);

    return lPoints
  }

  /**
   * 
   * @returns gets the M outline as an array of line segments
   */
   getMOutlineLineSegments(){
    let mLineSegments = new Array();
    
    mLineSegments.push(new LineSegment(this.x + this.cellSize, this.y, 
      this.x + 3 * this.cellSize, this.y + 2 * this.cellSize));

    mLineSegments.push(new LineSegment(this.x + 3 * this.cellSize, this.y + 2 * this.cellSize, 
      this.x + 5 * this.cellSize, this.y));

    mLineSegments.push(new LineSegment(this.x + 5 * this.cellSize, this.y, 
      this.x + 6 * this.cellSize, this.y + this.cellSize));

    mLineSegments.push(new LineSegment(this.x + 6 * this.cellSize, this.y + this.cellSize, 
      this.x + 6 * this.cellSize, this.y + 3 * this.cellSize));

    mLineSegments.push(new LineSegment(this.x + 6 * this.cellSize, this.y + 3 * this.cellSize, 
      this.x + 5 * this.cellSize, this.y + 4 * this.cellSize));

    mLineSegments.push(new LineSegment(this.x + 5 * this.cellSize, this.y + 4 * this.cellSize, 
      this.x + 4 * this.cellSize, this.y + 3 * this.cellSize));

    mLineSegments.push(new LineSegment(this.x + 4 * this.cellSize, this.y + 3 * this.cellSize, 
      this.x + 3 * this.cellSize, this.y + 4 * this.cellSize));

    mLineSegments.push(new LineSegment(this.x + 3 * this.cellSize, this.y + 4 * this.cellSize, 
      this.x + 2 * this.cellSize, this.y + 3 * this.cellSize));

    mLineSegments.push(new LineSegment(this.x + 2 * this.cellSize, this.y + 3 * this.cellSize, 
      this.x + this.cellSize, this.y + 4 * this.cellSize));

    mLineSegments.push(new LineSegment(this.x + this.cellSize, this.y + 4 * this.cellSize, 
      this.x, this.y + 3 * this.cellSize));

    mLineSegments.push(new LineSegment(this.x, this.y + 3 * this.cellSize, 
      this.x, this.y + this.cellSize));

    mLineSegments.push(new LineSegment(this.x, this.y + this.cellSize, 
      this.x + this.cellSize, this.y));

    return mLineSegments;
  }

  /**
   * 
   * @returns Gets the M outline as an array of points (each point is [x, y])
   */
  getMOutlinePoints(){
    let mPoints = new Array();

    // Top part
    mPoints.push([this.x, this.y + this.cellSize]);
    mPoints.push([this.x + this.cellSize, this.y]);
    mPoints.push([this.x + 2 * this.cellSize, this.y + this.cellSize]);
    mPoints.push([this.x + 3 * this.cellSize, this.y + 2 * this.cellSize]);
    mPoints.push([this.x + 4 * this.cellSize, this.y + this.cellSize]);
    mPoints.push([this.x + 5 * this.cellSize, this.y]);
    mPoints.push([this.x + 6 * this.cellSize, this.y + this.cellSize]);

    // Right part
    mPoints.push([this.x + 6 * this.cellSize, this.y + 2 * this.cellSize]);
    mPoints.push([this.x + 6 * this.cellSize, this.y + 3 * this.cellSize]);
    mPoints.push([this.x + 5 * this.cellSize, this.y + 4 * this.cellSize]);

    // Bottom part
    mPoints.push([this.x + 4 * this.cellSize, this.y + 3 * this.cellSize]);
    mPoints.push([this.x + 3 * this.cellSize, this.y + 4 * this.cellSize]);
    mPoints.push([this.x + 2 * this.cellSize, this.y + 3 * this.cellSize]);
    mPoints.push([this.x + 1 * this.cellSize, this.y + 4 * this.cellSize]);
    mPoints.push([this.x + 0 * this.cellSize, this.y + 3 * this.cellSize]);

    // Left part
    mPoints.push([this.x + 0 * this.cellSize, this.y + 2 * this.cellSize]);
    mPoints.push([this.x + 0 * this.cellSize, this.y + 1 * this.cellSize]);
   
    return mPoints;
  }

  /**
   * Gives each triangle its own random color from the Makeability Lab palette,
   * using that same color for both fill and stroke.
   *
   * @param {Triangle[]} triangles - The triangles to recolor.
   * @param {boolean} [isFillVisible=true] - Whether the fill is drawn.
   * @param {boolean} [isStrokeVisible=true] - Whether the stroke is drawn.
   */
  static setRandomColors(triangles, isFillVisible=true, isStrokeVisible=true){
    for(const tri of triangles){
      const fillColor = MakeabilityLabLogoColorer.getRandomOriginalColor();
      MakeabilityLabLogo.setColors([tri], fillColor, fillColor, isFillVisible, isStrokeVisible);
    }
  }

  /**
   * Sets the fill and stroke colors (and visibility) for the given triangles.
   * Also seeds startFillColor/endFillColor to the fill color so the triangles
   * are ready for color animation (see {@link MakeabilityLabLogoMorpher}).
   *
   * @param {Triangle[]} triangles - The triangles to recolor.
   * @param {string} fillColor - The fill color.
   * @param {string} strokeColor - The stroke color.
   * @param {boolean} [isFillVisible=true] - Whether the fill is drawn.
   * @param {boolean} [isStrokeVisible=true] - Whether the stroke is drawn.
   */
  static setColors(triangles, fillColor, strokeColor, isFillVisible=true, isStrokeVisible=true){
    for(const tri of triangles){
      tri.fillColor = fillColor;
      tri.startFillColor = fillColor;
      tri.endFillColor = fillColor;
      tri.strokeColor = strokeColor;
      tri.isFillVisible = isFillVisible;
      tri.isStrokeVisible = isStrokeVisible;
    }
  }

  static createMakeabilityLabLogoCellArray(x, y, triangleSize) {
    let mlLogo = new Array(MakeabilityLabLogo.numRows);

    // Initialize the make lab logo grid
    for (let row = 0; row < mlLogo.length; row++) {
      mlLogo[row] = new Array(MakeabilityLabLogo.numCols);
    }

    mlLogo[0] = MakeabilityLabLogo.createMakeabilityLabTopRow(x, y, triangleSize);

    y += triangleSize;
    mlLogo[1] = MakeabilityLabLogo.createMakeabilityLab2ndRow(x, y, triangleSize);

    y += triangleSize;
    mlLogo[2] = MakeabilityLabLogo.createMakeabilityLab3rdRow(x, y, triangleSize);

    y += triangleSize;
    mlLogo[3] = MakeabilityLabLogo.createMakeabilityLabBottomRow(x, y, triangleSize);

    return mlLogo;
  }

  static createMakeabilityLabTopRow(x, y, triangleSize) {
    let topRow = new Array(MakeabilityLabLogo.numCols);
    let col = 0;
    topRow[col++] = Cell.createCellWithBottomTriangleOnly(x, y, triangleSize, TriangleDir.BottomRight);

    x += triangleSize;
    topRow[col++] = Cell.createCellWithBottomTriangleOnly(x, y, triangleSize, TriangleDir.BottomLeft);

    x += triangleSize;
    topRow[col++] = Cell.createEmptyCell(x, y, triangleSize, TriangleDir.TopLeft);

    x += triangleSize;
    topRow[col++] = Cell.createEmptyCell(x, y, triangleSize, TriangleDir.TopRight);

    x += triangleSize;
    topRow[col++] = Cell.createCellWithBottomTriangleOnly(x, y, triangleSize, TriangleDir.BottomRight);

    x += triangleSize;
    topRow[col++] = Cell.createCellWithBottomTriangleOnly(x, y, triangleSize, TriangleDir.BottomLeft);

    return topRow;
  }

  static createMakeabilityLab2ndRow(x, y, triangleSize) {
    let row2 = new Array(MakeabilityLabLogo.numCols);
    let col = 0;
    row2[col++] = Cell.createCell(x, y, triangleSize, TriangleDir.TopRight, TriangleDir.BottomLeft);

    x += triangleSize;
    row2[col++] = Cell.createCell(x, y, triangleSize, TriangleDir.TopLeft, TriangleDir.BottomRight);

    x += triangleSize;
    row2[col++] = Cell.createCellWithBottomTriangleOnly(x, y, triangleSize, TriangleDir.BottomLeft);

    x += triangleSize;
    row2[col++] = Cell.createCellWithBottomTriangleOnly(x, y, triangleSize, TriangleDir.BottomRight);

    x += triangleSize;
    row2[col++] = Cell.createCell(x, y, triangleSize, TriangleDir.TopRight, TriangleDir.BottomLeft);

    x += triangleSize;
    row2[col++] = Cell.createCell(x, y, triangleSize, TriangleDir.TopLeft, TriangleDir.BottomRight);

    return row2;
  }

  static createMakeabilityLab3rdRow(x, y, triangleSize) {
    let row3 = new Array(MakeabilityLabLogo.numCols);
    for (let col = 0; col < row3.length; col++) {
      let triDir = TriangleDir.TopLeft;
      if (col % 2 !== 0) {
        triDir = TriangleDir.TopRight;
      }
      row3[col] = Cell.createCell(x, y, triangleSize, triDir);
      x += triangleSize;
    }
    return row3;
  }

  static createMakeabilityLabBottomRow(x, y, triangleSize) {
    let botRow = new Array(MakeabilityLabLogo.numCols);
    for (let col = 0; col < botRow.length; col++) {
      let triDir = TriangleDir.TopRight;
      if (col % 2 !== 0) {
        triDir = TriangleDir.TopLeft;
      }
      botRow[col] = Cell.createCellWithTopTriangleOnly(x, y, triangleSize, triDir);
      x += triangleSize;
    }
    return botRow;
  }

  /**
   * Determines if the given row, column, and triangle number correspond to an M shadow triangle.
   * See getMShadowTriangles() for more information.
   * 
   * @param {number} row - The row number to check.
   * @param {number} col - The column number to check.
   * @param {number} triNum - The triangle number to check.
   * @returns {boolean} - Returns true if the specified row, column, and triangle number 
   * form an M shadow triangle, otherwise false.
   */
  static isMShadowTriangle(row, col, triNum){
    return (row == 2 && col == 1 && triNum == 2) ||
          (row == 3 && col == 1 && triNum == 1) ||
          (row == 2 && col == 4 && triNum == 2) ||
          (row == 3 && col == 4 && triNum == 1);
  }

  /**
   * Determines if the specified row, column, and triangle number correspond to a 
   * triangle used in the L in the Makeability Lab logo
   * See getLTriangles() for more information.
   *
   * @param {number} row - The row index.
   * @param {number} col - The column index.
   * @param {number} triNum - The triangle number.
   * @returns {boolean} - Returns true if the specified row, column, and triangle number 
   *   correspond to an L-shaped triangle; otherwise, false.
   */
  static isLTriangle(row, col, triNum) {
    return (row == 0 && col == 0 && triNum == 2) ||
           (row == 0 && col == 1 && triNum == 2) ||
           (row == 1 && col == 0 && triNum == 1) ||
           (row == 1 && col == 1 && triNum == 1) ||
           (row == 1 && col == 1 && triNum == 2) ||
           (row == 1 && col == 2 && triNum == 2) ||
           (row == 2 && col == 1 && triNum == 1) ||
           (row == 2 && col == 2 && triNum == 1) ||
           (row == 2 && col == 2 && triNum == 2) ||
           (row == 3 && col == 2 && triNum == 1) ||
           (row == 3 && col == 3 && triNum == 1) ||
           (row == 2 && col == 3 && triNum == 1) ||
           (row == 2 && col == 3 && triNum == 2) ||
           (row == 1 && col == 3 && triNum == 2) ||
           (row == 2 && col == 4 && triNum == 1) ||
           (row == 1 && col == 4 && triNum == 2);
  }

}

export const TriangleDir = {
  TopLeft: 'TopLeft',
  TopRight: 'TopRight',
  BottomLeft: 'BottomLeft',
  BottomRight: 'BottomRight'
};

export class Cell {
  /**
   * Creates an instance of the class with two triangles.
   * @constructor
   * @param {Object} triangle1 - The first triangle object.
   * @param {Object} triangle2 - The second triangle object.
   */
  constructor(triangle1, triangle2) {
    this.tri1 = triangle1;
    this.tri2 = triangle2;

    this.drawBoundingBox = false; // for debugging
  }

  /**
   * Sets the draw debug information flag for this cell
   *
   * @param {boolean} drawDebugInfo - A flag indicating whether to draw debug information.
   */
  setDrawDebugInfo(drawDebugInfo){
    this.drawBoundingBox = drawDebugInfo;
    this.tri1.setDrawDebugInfo(drawDebugInfo);
    this.tri2.setDrawDebugInfo(drawDebugInfo);
  }

  /**
   * Gets the x-coordinate of the cell
   * @returns {number} The x-coordinate of the cell.
   */
  get x() {
    return this.tri1.x;
  }

  /**
   * Sets the x-coordinate for the cell
   * 
   * @param {number} x - The x-coordinate to set.
   */
  set x(x){
    this.tri1.x = x;
    this.tri2.x = x;
  }

  /**
   * Gets the y-coordinate of the cell
   * @returns {number} The y-coordinate of the cell.
   */
  get y() {
    return this.tri1.y;
  }

  /**
   * Sets the y-coordinate for the cell
   * 
   * @param {number} y - The y-coordinate to set.
   */
  set y(y){
    this.tri1.y = y;
    this.tri2.y = y;
  }

  /**
   * Gets the size of the cell. Cells are always square.
   * @type {number}
   */
  get size() {
    return this.tri1.size;
  }

  /**
   * Sets the size of the cell.
   * 
   * @param {number} size - The size to set for the cell.
   */
  set size(size){
    this.tri1.size = size;
    this.tri2.size = size;
  }


  /**
   * Sets the fill and stroke colors for the cell.
   *
   * @param {string} fillColor - The color to be used for filling.
   * @param {string} [strokeColor] - The color to be used for the stroke. 
   *    If not provided, the fillColor will be used as the stroke color.
   */
  setColors(fillColor, strokeColor){
    this.setFillColor(fillColor);

    if(strokeColor){
      this.setStrokeColor(strokeColor);
    }else{
      this.setStrokeColor(fillColor);
    }
  }

  /**
   * Sets the fill color for the cell.
   * 
   * @param {string} fillColor - The fill color
   */
  setFillColor(fillColor){
    this.tri1.fillColor = fillColor;
    this.tri2.fillColor = fillColor;
  }

  /**
   * Sets the stroke width for the logo's triangles.
   *
   * @param {number} strokeWidth - The width of the stroke to be applied to the triangles.
   */
  setStrokeWidth(strokeWidth){
    this.tri1.strokeWidth = strokeWidth;
    this.tri2.strokeWidth = strokeWidth;
  }

  /**
   * Sets the stroke color for the cell.
   * 
   * @param {string} strokeColor - The stroke color
   */
  setStrokeColor(strokeColor){
    this.tri1.strokeColor = strokeColor;
    this.tri2.strokeColor = strokeColor;
  }

  /**
   * Sets the visibility of the cell
   *
   * @param {boolean} isVisible - A boolean indicating whether the cell is visible
   */
  setVisibility(isVisible){
    this.tri1.visible = isVisible;
    this.tri2.visible = isVisible;
  }

  /**
   * Calculates the bounding box of the cell, encompassing all visible triangles.
   * If both triangles are visible, it returns the union of their bounding boxes.
   * If only one is visible, it returns that triangle's bounding box.
   *
   * @returns {Object} An object containing the {x, y, width, height} of the bounding box. 
   * Returns a zero-sized box {x:0, y:0, width:0, height:0} if no triangles are visible.
   */
  getBoundingBox() {
    const b1 = this.tri1.visible ? this.tri1.getBoundingBox() : null;
    const b2 = this.tri2.visible ? this.tri2.getBoundingBox() : null;
    if (b1 && b2) {
      const minX = Math.min(b1.x, b2.x);
      const minY = Math.min(b1.y, b2.y);
      const maxX = Math.max(b1.x + b1.width, b2.x + b2.width);
      const maxY = Math.max(b1.y + b1.height, b2.y + b2.height);
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
    return b1 || b2 || { x: 0, y: 0, width: 0, height: 0 };
  }

  /**
   * Draws the cells on the canvas
   *
   * @param {CanvasRenderingContext2D} ctx - The canvas rendering context to draw on.
   */
  draw(ctx) {
    if (this.tri1.visible) {
      this.tri1.draw(ctx);
    }

    if (this.tri2.visible) {
      this.tri2.draw(ctx);
    }

    ctx.save();
    if (this.drawBoundingBox){
      const boundingBox = this.getBoundingBox();
      ctx.setLineDash([3, 4]); // Dots of 3 pixel, gaps of 4 pixels
      ctx.strokeStyle = 'rgba(0, 0, 240, 0.5)';
      ctx.strokeRect(boundingBox.x, boundingBox.y, boundingBox.width, boundingBox.height);
    }
    ctx.restore();
  }

  /**
   * Creates an empty cell with two invisible triangles.
   *
   * @param {number} x - The x-coordinate of the cell.
   * @param {number} y - The y-coordinate of the cell.
   * @param {number} size - The size of the triangles.
   * @param {TriangleDir} [topTriangleDir=TriangleDir.TopLeft] - The direction of the top triangle.
   * @returns {Cell} A new cell containing two invisible triangles.
   */
  static createEmptyCell(x, y, size, topTriangleDir=TriangleDir.TopLeft) {
    let tri1 = new Triangle(x, y, size, topTriangleDir);
    let tri2 = new Triangle(x, y, size, Triangle.getOppositeDirection(topTriangleDir));
    tri1.visible = false;
    tri2.visible = false;
    return new Cell(tri1, tri2);
  }

  /**
   * Creates a cell with only the top triangle visible.
   *
   * @param {number} x - The x-coordinate of the cell.
   * @param {number} y - The y-coordinate of the cell.
   * @param {number} size - The size of the triangles.
   * @param {string} topTriangleDir - The direction of the top triangle. See TriangleDir for possible values.
   * @returns {Cell} A cell object with the top triangle visible and the bottom triangle hidden.
   */
  static createCellWithTopTriangleOnly(x, y, size, topTriangleDir) {
    let tri1 = new Triangle(x, y, size, topTriangleDir);
    let tri2 = new Triangle(x, y, size, Triangle.getOppositeDirection(topTriangleDir));
    tri2.visible = false;
    return new Cell(tri1, tri2);
  }

  /**
   * Creates a cell with only the bottom triangle visible.
   *
   * @param {number} x - The x-coordinate of the cell.
   * @param {number} y - The y-coordinate of the cell.
   * @param {number} size - The size of the triangles.
   * @param {string} botTriangleDir - The direction of the bottom triangle. See TriangleDir for possible values.
   * @returns {Cell} A new cell with the specified bottom triangle.
   */
  static createCellWithBottomTriangleOnly(x, y, size, botTriangleDir) {
    let tri1 = new Triangle(x, y, size, Triangle.getOppositeDirection(botTriangleDir));
    let tri2 = new Triangle(x, y, size, botTriangleDir);
    tri1.visible = false;
    return new Cell(tri1, tri2);
  }

  /**
   * Creates a cell composed of two triangles.
   *
   * @param {number} x - The x-coordinate of the cell.
   * @param {number} y - The y-coordinate of the cell.
   * @param {number} size - The size of the triangles.
   * @param {string} triangle1Dir - The direction of the first triangle. See TriangleDir for possible values.
   * @param {string} [triangle2Dir] - The direction of the second triangle. If not provided, it will be the opposite of the first triangle's direction.
   * @returns {Cell} A new cell composed of two triangles.
   */
  static createCell(x, y, size, triangle1Dir, triangle2Dir) {
    let tri1 = new Triangle(x, y, size, triangle1Dir);

    if (!triangle2Dir) {
      triangle2Dir = Triangle.getOppositeDirection(triangle1Dir);
    }
    let tri2 = new Triangle(x, y, size, triangle2Dir);
    return new Cell(tri1, tri2);
  }
}

export class Triangle {
  /**
   * Creates an instance of the triangle.
   * 
   * @constructor
   * @param {number} x - The x-coordinate of the triangle.
   * @param {number} y - The y-coordinate of the triangle.
   * @param {number} size - The size of the equilateral triangle.
   * @param {string} direction - The direction of the triangle. See TriangleDir for possible values.
   * @param {string} [fillColor='white'] - The fill color of the triangle.
   * @param {string} [strokeColor='black'] - The stroke color of the triangle.
   * @param {number} [strokeWidth=1] - The stroke width of the triangle.
   * @param {boolean} [visible=true] - The visibility of the triangle.
   */
  constructor(x, y, size, direction, fillColor = 'white',
    strokeColor = 'black', strokeWidth = 1, visible = true) {
    this.x = x;
    this.y = y;
    this.size = size;
    this.direction = direction;
    this.angle = 0;

    this.strokeColor = strokeColor;
    this.fillColor = fillColor;
    this.strokeWidth = strokeWidth;
    this.visible = visible;

    this.isFillVisible = true;
    this.isStrokeVisible = true;

    this.drawCellOutline = false; // for debugging
    this.drawBoundingBox = false; // for debugging
  }

  /**
   * Sets the draw debug information flag for this triangle
   *
   * @param {boolean} drawDebugInfo - A flag indicating whether to draw debug information.
   */
  setDrawDebugInfo(drawDebugInfo){
    this.drawCellOutline = drawDebugInfo; 
    this.drawBoundingBox = drawDebugInfo;
  }

  /**
   * Sets the fill and stroke colors for the triangle.
   *
   * @param {string} fillColor - The color to be used for filling.
   * @param {string} [strokeColor] - The color to be used for the stroke. If not provided, the fillColor will be used as the stroke color.
   */
  setColors(fillColor, strokeColor){
    this.fillColor = fillColor;

    if(strokeColor){
      this.strokeColor = strokeColor;
    }else{
      this.strokeColor = fillColor;
    }
  }

  /**
   * Draws a triangle on the given canvas context based on the object's properties.
   *
   * @param {CanvasRenderingContext2D} ctx - The canvas rendering context to draw on.
   */
  draw(ctx) {
    if (!this.visible) return;

    ctx.save();

    if (this.isFillVisible && this.fillColor) {
      ctx.fillStyle = this.fillColor;
    }

    if (this.isStrokeVisible && this.strokeColor && this.strokeWidth > 0) {
      ctx.strokeStyle = this.strokeColor;
      ctx.lineWidth = this.strokeWidth;
    } 

    ctx.translate(this.x, this.y);
    ctx.rotate(this.angle * Math.PI / 180);

    ctx.beginPath();
    switch (this.direction) {
      case TriangleDir.BottomLeft:
        ctx.moveTo(0, 0);
        ctx.lineTo(0, this.size);
        ctx.lineTo(this.size, this.size);
        break;
      case TriangleDir.BottomRight:
        ctx.moveTo(0, this.size);
        ctx.lineTo(this.size, this.size);
        ctx.lineTo(this.size, 0);
        break;
      case TriangleDir.TopRight:
        ctx.moveTo(0, 0);
        ctx.lineTo(this.size, 0);
        ctx.lineTo(this.size, this.size);
        break;
      case TriangleDir.TopLeft:
      default:
        ctx.moveTo(0, this.size);
        ctx.lineTo(0, 0);
        ctx.lineTo(this.size, 0);
        break;
    }
    ctx.closePath();

    if (this.isFillVisible && this.fillColor) {
      ctx.fill();
    }

    if (this.isStrokeVisible && this.strokeColor && this.strokeWidth > 0) {
      ctx.stroke();
    }

    // useful for debugging
    if (this.drawCellOutline) {
      ctx.setLineDash([2, 3]); // Dots of 2 pixel, gaps of 3 pixels
      ctx.strokeStyle = 'rgba(0, 200, 0, 0.5)';
      ctx.strokeRect(0, 0, this.size, this.size);
    }

    ctx.restore();

    ctx.save();
    if (this.drawBoundingBox){
      const boundingBox = this.getBoundingBox();
      ctx.setLineDash([1, 5]); // Dots of 1 pixel, gaps of 5 pixels
      ctx.strokeStyle = 'rgba(200, 0, 30, 0.8)';
      ctx.strokeRect(boundingBox.x, boundingBox.y, boundingBox.width, boundingBox.height);
    }
    ctx.restore();
  }

  /**
   * Calculates the bounding box of the triangle, taking into account its location, 
   * size, and angle
   *
   * @returns {Object} An object containing the coordinates of the bounding box.
   */
  getBoundingBox() {
    const rad = this.angle * Math.PI / 180;

    // Calculate the vertices of the triangle based on its direction
    let vertices;
    switch (this.direction) {
      case TriangleDir.BottomLeft:
        vertices = [
          { x: 0, y: 0 },
          { x: 0, y: this.size },
          { x: this.size, y: this.size }
        ];
        break;
      case TriangleDir.BottomRight:
        vertices = [
          { x: 0, y: this.size },
          { x: this.size, y: this.size },
          { x: this.size, y: 0 }
        ];
        break;
      case TriangleDir.TopRight:
        vertices = [
          { x: 0, y: 0 },
          { x: this.size, y: 0 },
          { x: this.size, y: this.size }
        ];
        break;
      case TriangleDir.TopLeft:
      default:
        vertices = [
          { x: 0, y: this.size },
          { x: 0, y: 0 },
          { x: this.size, y: 0 }
        ];
        break;
    }

    // Rotate the vertices around the origin and translate to the triangle's position
    const rotatedVertices = vertices.map(vertex => {
      const rotatedX = vertex.x * Math.cos(rad) - vertex.y * Math.sin(rad);
      const rotatedY = vertex.x * Math.sin(rad) + vertex.y * Math.cos(rad);
      return {
        x: this.x + rotatedX,
        y: this.y + rotatedY
      };
    });

    // Find the min and max x and y values
    const minX = Math.min(...rotatedVertices.map(vertex => vertex.x));
    const maxX = Math.max(...rotatedVertices.map(vertex => vertex.x));
    const minY = Math.min(...rotatedVertices.map(vertex => vertex.y));
    const maxY = Math.max(...rotatedVertices.map(vertex => vertex.y));

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  /**
   * Returns the opposite direction of the given triangle direction.
   *
   * @param {TriangleDir} triangleDir - The current direction of the triangle.
   * @returns {TriangleDir} - The opposite direction of the given triangle direction.
   */
  static getOppositeDirection(triangleDir) {
    switch (triangleDir) {
      case TriangleDir.BottomLeft:
        return TriangleDir.TopRight;
      case TriangleDir.BottomRight:
        return TriangleDir.TopLeft;
      case TriangleDir.TopRight:
        return TriangleDir.BottomLeft;
      case TriangleDir.TopLeft:
      default:
        return TriangleDir.BottomRight;
    }
  }

  /**
   * Creates a new Triangle object with the specified properties.
   *
   * @param {Object} tri - An object containing the properties of the triangle.
   * @param {number} tri.x - The x-coordinate of the triangle.
   * @param {number} tri.y - The y-coordinate of the triangle.
   * @param {number} tri.size - The size of the triangle.
   * @param {string} tri.direction - The direction of the triangle.
   * @param {string} tri.fillColor - The fill color of the triangle.
   * @param {string} tri.strokeColor - The stroke color of the triangle.
   * @param {number} tri.strokeWidth - The stroke width of the triangle.
   * @param {boolean} tri.visible - The visibility of the triangle.
   * @returns {Triangle} A new Triangle object.
   */
  static createTriangle(tri){
    return new Triangle(tri.x, tri.y, tri.size, tri.direction,
      tri.fillColor, tri.strokeColor, tri.strokeWidth, tri.visible);
  }
}

export class Grid {

  /**
   * Creates a background grid of triangular cells covering a canvas area.
   *
   * By default the grid starts at (0, 0), which is fine when the logo also
   * starts at (0, 0). However, when the logo is centered or offset — for
   * example after calling fitToCanvas(), or when manually centering via
   * getGridXCenterPosition() / getGridYCenterPosition() — the grid origin
   * must match the logo's origin so cell boundaries stay aligned.
   *
   * Pass the logo's x and y as offsetX / offsetY to achieve this:
   *
   * @example
   * // Manually positioned logo
   * const xLogo = MakeabilityLabLogo.getGridXCenterPosition(triangleSize, canvasWidth);
   * const yLogo = MakeabilityLabLogo.getGridYCenterPosition(triangleSize, canvasHeight);
   * const logo  = new MakeabilityLabLogo(xLogo, yLogo, triangleSize);
   * const grid  = new Grid(canvasWidth, canvasHeight, triangleSize,
   *                        undefined, undefined, xLogo, yLogo);
   *
   * @example
   * // fitToCanvas — logo origin is set internally, read it back afterward
   * const logo = new MakeabilityLabLogo(0, 0, triangleSize);
   * logo.fitToCanvas(canvasWidth, canvasHeight);
   * const grid = new Grid(canvasWidth, canvasHeight, logo.cellSize,
   *                       undefined, undefined, logo.x, logo.y);
   *
   * @constructor
   * @param {number} gridWidth   - Canvas width in logical (CSS) pixels.
   * @param {number} gridHeight  - Canvas height in logical (CSS) pixels.
   * @param {number} triangleSize - Cell size in pixels; should match the logo's
   *   cellSize so grid lines fall on logo cell boundaries.
   * @param {string} [strokeColor='rgba(200, 200, 200, 0.5)'] - Grid line color.
   * @param {string|null} [fillColor=null] - Optional fill color for all cells.
   * @param {number} [offsetX=0] - X origin of the grid. Pass the logo's x so the
   *   grid is phase-aligned with the logo's cell columns.
   * @param {number} [offsetY=0] - Y origin of the grid. Pass the logo's y so the
   *   grid is phase-aligned with the logo's cell rows.
   */
  constructor(gridWidth, gridHeight, triangleSize,
              strokeColor = 'rgba(200, 200, 200, 0.5)', fillColor = null,
              offsetX = 0, offsetY = 0) {

    this.gridArray = Grid.createGrid(gridWidth, gridHeight, triangleSize,
                                     strokeColor, fillColor, offsetX, offsetY);
    this.visible = true;

    // setFillColor is a no-op when fillColor is null, but calling it here
    // keeps construction consistent if a subclass overrides it later.
    this.setFillColor(fillColor);
  }

  /**
   * Draws the grid onto the provided canvas context.
   *
   * @param {CanvasRenderingContext2D} ctx - The canvas rendering context to draw on.
   */
  draw(ctx) {
    if (!this.visible) { return; }

    for (let row = 0; row < this.gridArray.length; row++) {
      for (let col = 0; col < this.gridArray[row].length; col++) {
        this.gridArray[row][col].draw(ctx);
      }
    }
  }

  /**
   * Sets the stroke color for all triangles in the grid.
   *
   * @param {string} strokeColor - The new stroke color.
   */
  setStrokeColor(strokeColor) {
    for (let row = 0; row < this.gridArray.length; row++) {
      for (let col = 0; col < this.gridArray[row].length; col++) {
        this.gridArray[row][col].tri1.strokeColor = strokeColor;
        this.gridArray[row][col].tri2.strokeColor = strokeColor;
      }
    }
  }

  /**
   * Sets the fill color for all triangles in the grid.
   *
   * @param {string|null} fillColor - The new fill color, or null for transparent.
   */
  setFillColor(fillColor) {
    for (let row = 0; row < this.gridArray.length; row++) {
      for (let col = 0; col < this.gridArray[row].length; col++) {
        this.gridArray[row][col].tri1.fillColor = fillColor;
        this.gridArray[row][col].tri2.fillColor = fillColor;
      }
    }
  }

  /**
   * Creates a 2D array of triangular cells that tile the canvas.
   *
   * The grid is extended by one extra row and column beyond what fits in
   * gridWidth/gridHeight to ensure full coverage when offsetX/offsetY shift
   * the origin away from (0, 0) — otherwise a strip along the top/left edge
   * would be left uncovered.
   *
   * Triangle orientation alternates in a checkerboard pattern so the diagonal
   * cuts form the characteristic diamond/X visual of the Makeability Lab grid.
   * The parity of each cell is determined by its logical row/col index (not its
   * pixel position), so the pattern stays consistent regardless of the offset.
   *
   * @param {number} gridWidth    - Canvas width in logical pixels.
   * @param {number} gridHeight   - Canvas height in logical pixels.
   * @param {number} triangleSize - Cell size in pixels.
   * @param {string} strokeColor  - Stroke color for all cells.
   * @param {string|null} fillColor - Fill color for all cells, or null.
   * @param {number} [offsetX=0]  - X origin offset in pixels.
   * @param {number} [offsetY=0]  - Y origin offset in pixels.
   * @returns {Array<Array<Cell>>} 2D array of Cell objects.
   */
  static createGrid(gridWidth, gridHeight, triangleSize,
                  strokeColor, fillColor,
                  offsetX = 0, offsetY = 0) {

    // Backtrack from the offset to find the first cell origin that is <= 0,
    // so the grid covers the full canvas including the top and left edges.
    // e.g. offsetX=50, triangleSize=100 → startX = 50 - 1*100 = -50
    const startX = offsetX - Math.ceil(offsetX / triangleSize) * triangleSize;
    const startY = offsetY - Math.ceil(offsetY / triangleSize) * triangleSize;

    // Size the grid from the adjusted start point to ensure full coverage.
    const numGridColumns = Math.ceil((gridWidth  - startX) / triangleSize);
    const numGridRows    = Math.ceil((gridHeight - startY) / triangleSize);

    let grid = new Array(numGridRows);

    for (let row = 0; row < grid.length; row++) {
      grid[row] = new Array(numGridColumns);

      for (let col = 0; col < grid[row].length; col++) {

        const cellX = startX + col * triangleSize;
        const cellY = startY + row * triangleSize;

        // Checkerboard parity: determine orientation from absolute cell index
        // so the pattern is consistent across the whole canvas regardless of
        // where the grid starts. We use the cell's position relative to the
        // logo origin (offsetX/offsetY) to compute the canonical index.
        const colIndex = Math.round((cellX - offsetX) / triangleSize);
        const rowIndex = Math.round((cellY - offsetY) / triangleSize);
        const isEvenCell = (rowIndex % 2 === 0 && colIndex % 2 === 0) ||
                          (rowIndex % 2 !== 0 && colIndex % 2 !== 0);
        const triDir = isEvenCell ? TriangleDir.TopRight : TriangleDir.TopLeft;

        const cell = Cell.createCell(cellX, cellY, triangleSize, triDir);

        cell.tri1.strokeColor = strokeColor;
        cell.tri2.strokeColor = strokeColor;

        if (fillColor) {
          cell.tri1.fillColor = fillColor;
          cell.tri2.fillColor = fillColor;
        }

        grid[row][col] = cell;
      }
    }

    return grid;
  }
}

export const OriginalColorPaletteRGB = {
  Blue: "rgb(135, 202, 228)",
  BlueGray: "rgb(147, 169, 207)",
  Purple: "rgb(171, 147, 197)",
  Green: "rgb(148, 206, 146)",
  Orange: "rgb(235, 185, 130)",
  RedPurple: "rgb(207, 145, 166)",
  Pink: "rgb(237, 162, 163)",
  YellowGreen: "rgb(239, 226, 127)",
  LightGreen: "rgb(209, 226, 133)",
  BlueGreen: "rgb(147, 211, 202)"
};

export const ORIGINAL_COLOR_ARRAY = [
  OriginalColorPaletteRGB.Blue, 
  OriginalColorPaletteRGB.BlueGray,
  OriginalColorPaletteRGB.YellowGreen,
  OriginalColorPaletteRGB.Purple,
  OriginalColorPaletteRGB.Green,
  OriginalColorPaletteRGB.Orange,
  OriginalColorPaletteRGB.YellowGreen,
  OriginalColorPaletteRGB.LightGreen,
  OriginalColorPaletteRGB.Orange,
  OriginalColorPaletteRGB.RedPurple,
  OriginalColorPaletteRGB.BlueGreen,
  OriginalColorPaletteRGB.Pink
];


/**
 * Class representing a colorer for the Makeability Lab logo.
 */
export class MakeabilityLabLogoColorer {

  /**
   * Gets a random color from the original color palette.
   * @returns {string} A random color in RGB format from the original color palette.
   */
  static getRandomOriginalColor(){
    return MakeabilityLabLogoColorer.getRandomColorFromPalette(OriginalColorPaletteRGB);
  }

  /**
   * Gets a random color from the specified color palette.
   * If no palette is provided, it defaults to the original color palette.
   * @param {Object} [palette] - An optional color palette object where keys are color names and values are RGB strings.
   * @returns {string} A random color in RGB format from the specified or default color palette.
   */
  static getRandomColorFromPalette(palette){
    if(!palette){
      palette = OriginalColorPaletteRGB;
    }

    const keys = Object.keys(palette);
    const randKey = keys[Math.floor(Math.random() * keys.length)];
    return palette[randKey];
  }
}